import { CompositionNormalizer } from '@src/components/sidepanel/compositionNormalizer/compositionNormalizer';
import {
  CompositionPlacement,
  CompositionType,
  DefaultBrandKitValue,
  QuoteControlType,
} from '@src/components/sidepanel/CompositionSettings/types';
import { TimelineMode } from '@src/editor/commands/TimelineMode';
import { calculateSectionInsertionTime } from '@src/services/composition/CompositionElementService';
import { normalizeTemplateControls } from '@src/services/composition/templateControlNormalization';
import { pruneUnusedSourceFonts } from '@src/services/composition/templateFontPruning';
import { transcriptSpeakerService } from '@src/services/transcript/TranscriptSpeakerService';
import { AdditionalAspectRatio, AspectRatio } from '@src/types.ts/video';
import {
  getElementMetaObject,
  storeCompositionTemplateMetadata,
  upsertElementMetaObject,
} from '@src/utility/extraElementData';
import { getCustomFontFromPostScriptName } from '@src/utility/fonts';
import { LogManager } from '@src/utility/Logger';

import { speakerNameLine, speakerTitleLine } from './speakerRole';

import type { CompositionActions } from '@src/components/sidepanel/CompositionContext';
import type {
  CompositionTemplate,
  ControlConfiguration,
  SpeakerBindingKey,
  SpeakerControlConfiguration,
} from '@src/components/sidepanel/CompositionSettings/types';
import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { RippleConfig } from '@src/editor/lib/TrackManager';
import type { Element } from '@src/editor/models/Element';
import type RendererSyncService from '@src/editor/services/RendererSyncService';
import type { ElementState } from '@src/renderer/ElementState';
import type { CompositionRepository } from '@src/repositories';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';
import type { DatoClientStore } from '@src/stores-v2';
import type { BrandingCustomFont, Showcase, Story } from '@src/types.ts/story';
import type { TranscriptElementWithClipSegmentAttributes } from '@src/types.ts/transcript';
import type { ExtendedAspectRatio } from '@src/types.ts/video';

export interface EditorStoreLike {
  editor: IVideoEditor;
  selectElement: (id: string) => void;
  rendererSyncService?: RendererSyncService;
}

type ConvertToVhFn = (val: string) => unknown;

const logger = LogManager.getLogger('utility:compositionSettings');

// Default placeholder for headshot controls when the speaker has no headshot image
export const DEFAULT_HEADSHOT_PLACEHOLDER =
  'data:image/svg+xml,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" fill="none">' +
      '<rect width="200" height="200" rx="100" fill="%23E0E0E0"/>' +
      '<circle cx="100" cy="80" r="35" fill="%23BDBDBD"/>' +
      '<ellipse cx="100" cy="165" rx="55" ry="45" fill="%23BDBDBD"/>' +
      '</svg>',
  );

function maybeConvertFontSize(
  property: string,
  value: unknown,
  convertToVh?: ConvertToVhFn,
): Record<string, unknown> {
  if (property === 'font_size' && typeof value === 'number' && convertToVh) {
    return { font_size: convertToVh(String(value)) };
  }
  const obj: Record<string, unknown> = {};
  obj[property] = value;
  return obj;
}

export interface ElementLike {
  id?: string;
  elements?: ElementLike[];
  [k: string]: unknown; // allow dynamic property assignment for control defaults
}

// Shared organization shape for branding/logo helpers
type OrganizationContext =
  | {
      branding?: {
        primaryColors?: unknown;
        secondaryColors?: unknown;
        brandFonts?: Array<{ fontFamily?: string } | Record<string, unknown>>;
        customFonts?: BrandingCustomFont[] | null;
        organizationWebsite?: string;
      };
      organizationLogos?: { url: string }[];
    }
  | null
  | undefined;

function hasStringId(val: unknown): val is { id: string } {
  if (!val || typeof val !== 'object' || val === null) return false;
  // Use any intermediary to avoid unsafe assertions while meeting lint rule.
  const anyObj: any = val; // eslint-disable-line @typescript-eslint/no-explicit-any
  const idValue = anyObj.id;
  return typeof idValue === 'string';
}

export function isElementLike(val: unknown): val is ElementLike {
  if (!val || typeof val !== 'object' || val === null) return false;
  if ('elements' in val) {
    const anyObj: any = val; // eslint-disable-line @typescript-eslint/no-explicit-any
    const elsUnknown = anyObj.elements;
    if (elsUnknown !== undefined && !Array.isArray(elsUnknown)) return false;
  }
  return true;
}

export function resolveElementFromPath(
  root: ElementLike,
  path: number[],
): ElementLike | undefined {
  let current: ElementLike | undefined = root;
  for (const seg of path) {
    if (!current) return undefined;
    const children: ElementLike[] | undefined = Array.isArray(current.elements)
      ? current.elements.filter(isElementLike)
      : undefined;
    if (!children) return undefined;
    const idx = Number(seg);
    if (Number.isNaN(idx) || idx < 0 || idx >= children.length)
      return undefined;
    const nextCandidate: unknown = children[idx];
    current = isElementLike(nextCandidate) ? nextCandidate : undefined;
  }
  return current;
}

function resolveChildElementId(
  root: ElementLike,
  path: number[],
): string | undefined {
  const el = resolveElementFromPath(root, path);
  return el?.id;
}

export function modifyCompositionProperty(
  elementId: string,
  editorStore: EditorStoreLike,
  elementPaths: number[][],
  property: string,
  value: unknown,
  createUndoRedoEntry: boolean = true,
): boolean {
  if (!elementId) {
    logger.warn('No element ID provided for modifyCompositionProperty', {
      elementId,
    });
    return false;
  }
  // Look up root element from timeline by id
  // (we avoid storing a direct reference to keep this decoupled and testable)
  // eslint-disable-next-line no-restricted-syntax
  const rootRaw = editorStore.editor.timeline.getElement(elementId);

  if (!rootRaw || typeof rootRaw !== 'object' || !('id' in rootRaw)) {
    logger.warn('Active element not found for modifyCompositionProperty', {
      id: elementId,
    });
    return false;
  }
  if (!hasStringId(rootRaw)) {
    logger.warn('Root element failed structural refinement');
    return false;
  }
  if (!isElementLike(rootRaw)) {
    logger.warn('Root element failed element-like refinement');
    return false;
  }
  const root: ElementLike = rootRaw;
  const targetIds = elementPaths
    .map(p => resolveChildElementId(root, p))
    .filter((id): id is string => typeof id === 'string');

  if (targetIds.length === 0) {
    logger.warn('No valid target ids for modifyCompositionProperty', {
      elementPaths,
    });
    return false;
  }

  const props: Record<string, unknown> = {};
  props[property] = value;

  let anySucceeded = false;
  targetIds.forEach(id => {
    const ok = editorStore.editor.commandUpdateElement(
      id,
      props,
      createUndoRedoEntry,
    );
    if (ok) {
      anySucceeded = true;
    } else {
      logger.warn('Failed to update composition element property', {
        property,
        targetId: id,
      });
    }
  });
  return anySucceeded;
}

export function getDefaultVideoValue(
  control: ControlConfiguration,
  story?: Story,
): unknown {
  if (control.type !== 'video') return undefined;
  const videoUrlCandidate = story?.storyArtifactsVideo?.[0]?.url;
  return typeof videoUrlCandidate === 'string' && videoUrlCandidate.length > 0
    ? videoUrlCandidate
    : undefined;
}
/**
 * Resolve default brand logo image URL for an image control whose id === 'logo'.
 */
export function getDefaultImageOrLogoValue(
  control: ControlConfiguration,
  organization?: OrganizationContext,
): unknown {
  if (
    control.type !== 'image' &&
    control.type !== 'logo' &&
    control.type !== 'headshot'
  )
    return undefined;
  // Headshot controls should not default to an organization logo
  if (control.type === 'headshot' || /headshot/i.test(control.label))
    return undefined;
  // Explicit defaults (value or brandKit) opt out of the brand-kit logo fallback
  // so the resolution chain can return control.default.value, or stay undefined
  // and let applyDefaultControlValuesToSource preserve the source asset.
  const def = control.default;
  if (def && typeof def === 'object' && ('value' in def || 'brandKit' in def)) {
    return undefined;
  }
  const logoUrlCandidate = organization?.organizationLogos?.[0]?.url;
  return typeof logoUrlCandidate === 'string' && logoUrlCandidate.length > 0
    ? logoUrlCandidate
    : undefined;
}

/**
 * Resolve default primary/secondary brand color for a color control whose brandKit matches.
 * Accepts comma-delimited string or array; returns first non-empty trimmed token.
 */
export function getDefaultColorValue(
  control: ControlConfiguration,
  organization?: OrganizationContext,
): unknown {
  const def = control.default;
  if (!def) return undefined;
  if (control.type !== 'color') return undefined;
  const brandKey = def.brandKit;
  if (
    brandKey !== DefaultBrandKitValue.PRIMARY_COLOR &&
    brandKey !== DefaultBrandKitValue.SECONDARY_COLOR
  )
    return undefined;

  const branding = organization?.branding;
  if (!branding) return undefined;

  const raw =
    brandKey === DefaultBrandKitValue.PRIMARY_COLOR
      ? branding.primaryColors
      : branding.secondaryColors;
  const extractFirst = (val: unknown): string | undefined => {
    if (typeof val === 'string') {
      const token = val
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)[0];
      return token || undefined;
    }
    if (Array.isArray(val)) {
      const first = val
        .map(v => (typeof v === 'string' ? v.trim() : ''))
        .filter(Boolean)[0];
      return first || undefined;
    }
    return undefined;
  };
  return extractFirst(raw);
}

/**
 * Resolve default organization website URL for a text control with brandKit === 'website'.
 */
export function getDefaultWebsiteValue(
  control: ControlConfiguration,
  organization?: OrganizationContext,
): unknown {
  const def = control.default;
  if (!def) return undefined;
  if (control.type !== 'text') return undefined;
  if (def.brandKit !== DefaultBrandKitValue.WEBSITE) return undefined;
  const website = organization?.branding?.organizationWebsite;
  return typeof website === 'string' && website.length > 0
    ? website
    : undefined;
}

/**
 * Resolve default brand font family for a font control with brandKit === 'defaultFont'.
 */
export function getDefaultFontValue(
  control: ControlConfiguration,
  organization?: OrganizationContext,
): unknown {
  const def = control.default;
  if (!def) return undefined;
  if (
    control.type !== 'font' ||
    def.brandKit !== DefaultBrandKitValue.DEFAULT_FONT
  )
    return undefined;
  const fonts = Array.isArray(organization?.branding?.brandFonts)
    ? organization?.branding?.brandFonts
    : undefined;
  if (!fonts || fonts.length === 0) return undefined;
  const first = fonts[0];
  const hasFontFamily = (val: unknown): val is { fontFamily: unknown } =>
    !!val && typeof val === 'object' && val !== null && 'fontFamily' in val;
  if (hasFontFamily(first)) {
    const raw = first.fontFamily;
    if (typeof raw === 'string') return raw;
  }
  return undefined;
}
export const getDimensionSize = (
  dimension: string,
): { width: number; height: number } => {
  switch (dimension) {
    case AspectRatio.AR_1_1:
      return { width: 1080, height: 1080 };
    case AspectRatio.AR_16_9:
      return { width: 1920, height: 1080 };
    case AspectRatio.AR_9_16:
      return { width: 607, height: 1080 };
    case AdditionalAspectRatio.AR_4_5:
      return { width: 864, height: 1080 };
    default:
      return { width: 1080, height: 1080 };
  }
};

export const getAspectRatioFromSource = (
  source: Record<string, unknown>,
): ExtendedAspectRatio => {
  const width = source.width as number;
  const height = source.height as number;
  const aspectRatio = width / height;

  const epsilon = 0.05; // Small tolerance for floating point comparison

  const oneByOneAspectRatio = 1;
  const sixteenByNineAspectRatio = 16 / 9;
  const nineBySixteenAspectRatio = 9 / 16;
  const fourByFiveAspectRatio = 4 / 5;

  if (Math.abs(aspectRatio - oneByOneAspectRatio) < epsilon) {
    return AspectRatio.AR_1_1;
  }
  if (Math.abs(aspectRatio - sixteenByNineAspectRatio) < epsilon) {
    return AspectRatio.AR_16_9;
  }
  if (Math.abs(aspectRatio - nineBySixteenAspectRatio) < epsilon) {
    return AspectRatio.AR_9_16;
  }
  if (Math.abs(aspectRatio - fourByFiveAspectRatio) < epsilon) {
    return AdditionalAspectRatio.AR_4_5;
  }

  return AspectRatio.AR_1_1;
};

// ============================================================================
// Quote card-specific functions moved to quoteCardsCompositionSettings.ts
// ============================================================================
// The following functions have been moved to keep compositionSettings generic:
// - getDefaultQuoteTextValue
// - getDefaultQuoteNameValue
// - getDefaultQuoteOrgValue
// - getDefaultQuoteBackgroundValue
// - getQuoteValue (renamed to getQuoteCardValueOverrides)
// - Quote dimension logic (now in applyQuoteCardSourceModifications)
//
// For quote cards, use:
// - getQuoteCardValueOverrides() to compute value overrides
// - applyQuoteCardSourceModifications() to apply dimension changes
// ============================================================================
/**
 * Dispatcher to obtain a specialized default value for a control (logo/color/font) or fall back to control.default.value.
 */
export function getDefaultControlValue(
  control: ControlConfiguration,
  organization?: OrganizationContext,
  story?: Story,
): unknown {
  const videoVal = getDefaultVideoValue(control, story);
  if (videoVal !== undefined) return videoVal;
  const imageVal = getDefaultImageOrLogoValue(control, organization);
  if (imageVal !== undefined) return imageVal;
  const colorVal = getDefaultColorValue(control, organization);
  if (colorVal !== undefined) return colorVal;
  const fontVal = getDefaultFontValue(control, organization);
  if (fontVal !== undefined) return fontVal;
  const websiteVal = getDefaultWebsiteValue(control, organization);
  if (websiteVal !== undefined) return websiteVal;
  return control.default?.value;
}

// getQuoteValue moved to quoteCardsCompositionSettings.ts as getQuoteCardValueOverrides

// ---------------------------------------------------------------------------
// Unified value resolution for controls (extracted from CompositionControl)
// ---------------------------------------------------------------------------
/**
 * Compute a unified value (and font weight if relevant) for a control across all target element paths.
 * Speaker controls derive their unified value from extraElementData speakerLabel meta.
 */
export function getUnifiedValue(
  control: ControlConfiguration,
  activeElement: ElementState,
  extraElementData?: Record<string, unknown>,
): { unifiedValue: unknown; unifiedFontWeight?: string } {
  const traverse = (
    root: unknown,
    path: number[],
  ): Record<string, unknown> | undefined => {
    let node: unknown = root;
    for (const segment of path) {
      if (!node || typeof node !== 'object') return undefined;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const els = (node as any).elements;
      if (!Array.isArray(els)) return undefined;
      const idx = Number(segment);
      if (idx < 0 || idx >= els.length) return undefined;
      node = els[idx];
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const src = (node as any)?.source;
    if (src && typeof src === 'object') return src as Record<string, unknown>;
    return undefined;
  };

  const sources: Record<string, unknown>[] = Array.isArray(control.elementPaths)
    ? control.elementPaths
        .map(p => traverse(activeElement, p))
        .filter((s): s is Record<string, unknown> => !!s)
    : [];

  let unifiedValue: unknown;
  if (control.type === 'speaker') {
    try {
      const activeId = (activeElement as { source?: { id?: string } })?.source
        ?.id;
      if (extraElementData && typeof activeId === 'string') {
        const meta = getElementMetaObject({ extraElementData }, activeId);
        unifiedValue = meta?.speakerLabel;
      }
    } catch {
      unifiedValue = undefined;
    }
  } else if (sources.length > 0) {
    const rawValues = sources
      .map(s => s[control.property])
      .filter(v => v !== undefined);
    if (rawValues.length > 0) {
      const unique = Array.from(
        new Set(
          rawValues.map(v =>
            typeof v === 'string' || typeof v === 'number'
              ? v
              : JSON.stringify(v),
          ),
        ),
      );
      if (unique.length === 1) unifiedValue = rawValues[0];
    }
  }

  // Fallback for font_size only (PLAT-10385): a nested text element can
  // inherit its size from the composition root, so read it there. No other
  // property inherits that way — the root's own fill_color / font_family is a
  // different element's value and reading it reported a sibling's styling as
  // this control's (PLAT-11977). The root must BE a composition: when a child
  // (the quote mark, say) is the selected element, its own font size is not
  // what every other text control inherits, and reading it there wrote one
  // element's size as the standard for all of them.
  if (
    unifiedValue === undefined &&
    control.type !== 'speaker' &&
    control.property === 'font_size' &&
    activeElement?.source?.type === 'composition'
  ) {
    const rootValue = activeElement?.source?.[control.property];
    if (rootValue !== undefined && rootValue !== null) {
      unifiedValue = rootValue;
    }

    if (unifiedValue === undefined) {
      // DEBUG: Log when font_size lookup fails completely
      logger.warn(
        'getUnifiedValue: Could not find value for font_size control',
        {
          property: control.property,
          elementPaths: control.elementPaths,
          sourcesFound: sources.length,
          activeElementId: (activeElement as { source?: { id?: string } })
            ?.source?.id,
          hasElements: !!activeElement.elements,
          elementCount: Array.isArray(activeElement.elements)
            ? activeElement.elements.length
            : 0,
          rootSourceValue: rootValue,
        },
      );
    }
  }

  let unifiedFontWeight: string | undefined;
  if (control.type === 'font' && sources.length > 0) {
    const fontWeights = sources
      .map(s => s['font_weight'])
      .filter((w): w is string => typeof w === 'string');
    const uniqueFw = Array.from(new Set(fontWeights));
    if (uniqueFw.length === 1) unifiedFontWeight = fontWeights[0];
  }

  return { unifiedValue, unifiedFontWeight };
}

/**
 * Calculate which control values have been modified from their defaults.
 * Returns an array of updated values with their element paths, values, and properties.
 * Useful for tracking changes and enabling save functionality.
 */
/** A single tracked control-value change — the unit of `updatedValues`. */
export type ControlValueUpdate = {
  elementPaths: number[][];
  value: unknown;
  property: string;
};

/**
 * Upsert a control-value update into a list, keyed by property + elementPaths
 * (latest value wins). Lets a direct edit (modifyProperty) and the value-diff
 * recompute merge without dropping either. Shared by the Creator Studio and Pull
 * Quotes settings panels.
 */
export const upsertUpdatedValue = (
  list: ControlValueUpdate[],
  entry: ControlValueUpdate,
): ControlValueUpdate[] => [
  ...list.filter(
    uv =>
      !(
        uv.property === entry.property &&
        JSON.stringify(uv.elementPaths) === JSON.stringify(entry.elementPaths)
      ),
  ),
  entry,
];

export function calculateUpdatedControlValues({
  controls,
  activeElement,
  extraElementData,
  organization,
  story,
}: {
  controls: ControlConfiguration[] | undefined;
  activeElement: ElementState;
  extraElementData?: Record<string, unknown>;
  organization?: OrganizationContext;
  story?: Story;
}): {
  elementPaths: number[][];
  value: unknown;
  property: string;
}[] {
  const updatedValues: {
    elementPaths: number[][];
    value: unknown;
    property: string;
  }[] = [];

  if (!controls) return updatedValues;

  // Controls whose default value is a "quote" (template/story quote metadata) are
  // intentionally excluded from updated values, as they are not user-editable fields
  // and should not be compared or persisted like regular control values.
  controls
    .filter(control => control.enabled !== false && !control?.default?.quote)
    .forEach(control => {
      const { unifiedValue } = getUnifiedValue(
        control,
        activeElement,
        extraElementData,
      );

      let valueToCompare: unknown = control.customValue ?? control.default;

      if (
        valueToCompare &&
        typeof valueToCompare === 'object' &&
        'brandKit' in valueToCompare
      ) {
        valueToCompare = getDefaultControlValue(control, organization, story);
      } else if (
        valueToCompare &&
        typeof valueToCompare === 'object' &&
        'value' in valueToCompare
      ) {
        valueToCompare = valueToCompare.value;
      }
      // A live value the renderer cannot report is "unknown", not "cleared".
      // Recording it as a change would carry `undefined` into the modal (an
      // After of "—" above a real Before) and then into the save, where it
      // erased the saved standard (PLAT-11977). Clearing stays possible through
      // an explicit '' from the modal's inputs or a recommendation rule.
      if (unifiedValue === undefined) return;
      if (
        control.type !== 'speaker' &&
        !controlValuesEqual(control, unifiedValue, valueToCompare)
      ) {
        updatedValues.push({
          elementPaths: control.elementPaths,
          value: unifiedValue,
          property: control.property,
        });
      }
    });

  return updatedValues;
}

/**
 * True when an updated-value entry refers to the same control, matched by
 * `property` + element-by-element `elementPaths` equality. Extracted from the
 * merge loop in CompositionSaveTemplate so the Apply Standards preview and the
 * save path share one matcher.
 */
export function matchesControl(
  entry: { property: string; elementPaths: number[][] },
  control: { property: string; elementPaths: number[][] },
): boolean {
  return (
    entry.property === control.property &&
    entry.elementPaths.length === control.elementPaths.length &&
    entry.elementPaths.every(
      (path, idx) =>
        path.length === control.elementPaths[idx]?.length &&
        path.every((p, pIdx) => p === control.elementPaths[idx][pIdx]),
    )
  );
}

/**
 * Apply control overrides to a template's controls for a save: each control
 * whose override carries a **defined** `value` gets it as `customValue`; every
 * other control is returned as-is, so a save only ever changes the controls
 * the user (or a rule) actually set. An override whose value is `undefined`
 * is a lookup that failed, never a request to clear — clearing is an explicit
 * `''` (PLAT-11977). Returns new objects; the input is not mutated.
 */
export function applyControlOverrides(
  controls: ControlConfiguration[],
  overrides: ControlOverride[],
): ControlConfiguration[] {
  return controls.map(control => {
    const override = overrides.find(entry => matchesControl(entry, control));
    if (!override) return control;
    let next = control;
    if (override.value === null) {
      // `null` removes the standard, so the control falls back to its default
      // (a font size back to auto-fit) — never an empty value on every new
      // card (PLAT-11993).
      if ('customValue' in next) {
        const { customValue: _removed, ...rest } = next;
        next = rest;
      }
    } else if (override.value !== undefined) {
      next = { ...next, customValue: override.value };
    }
    if (override.enabled !== undefined) {
      next = { ...next, enabled: override.enabled };
    }
    if (override.controlBindings !== undefined && next.type === 'speaker') {
      next = { ...next, controlBindings: { ...override.controlBindings } };
    }
    // A custom label is the control's own (PLAT-11993); normalization reads
    // it back on save. '' clears it so the label is derived from the element
    // name again.
    if (override.customLabel !== undefined) {
      const custom = override.customLabel.trim();
      if (custom) {
        next = { ...next, customLabel: custom, label: custom };
      } else if ('customLabel' in next) {
        const { customLabel: _cleared, ...rest } = next;
        next = rest;
      }
    }
    return next;
  });
}

export interface StandardsSuggestion {
  /** The recommended value. An empty string means "clear / remove this field". */
  value: unknown;
  /** Human-readable rationale shown to the user (the "why"). */
  reason: string;
}

/**
 * Rule-based Apply Standards recommendations. Returns a map of control →
 * suggested value + reason. Rules are deterministic and approved one at a time.
 *
 * Rules:
 * 1. **Hide speaker on intro/outro cards** — on `introCard` / `endCard`
 *    compositions, clear the speaker-identity fields (Name, Title/Org, headshot),
 *    which those card types usually omit. Styling controls (color/font) are left
 *    alone. Speaker-identity controls are the ones a speaker control binds to
 *    (its `controlBindings` keys are those controls' ids) plus any quote
 *    name/org/headshot fields.
 */
export function computeStandardsSuggestions(
  composition: Pick<CompositionTemplate, 'compositionType'>,
  controls: ControlConfiguration[],
  isPullQuote: boolean = false,
): Map<ControlConfiguration, StandardsSuggestion> {
  const suggestions = new Map<ControlConfiguration, StandardsSuggestion>();

  // The "hide speaker" rule is a Creator Studio (video intro/outro) concept.
  // Content Studio pull quotes attribute a quote to a person, so they keep the
  // speaker (Name/Role) — never recommend removing it there, whatever the
  // template's compositionType happens to be.
  const isIntroOrOutro =
    composition.compositionType === CompositionType.INTRO_CARD ||
    composition.compositionType === CompositionType.END_CARD;

  if (isIntroOrOutro && !isPullQuote) {
    const reason = "Intro and outro cards usually don't include speaker info.";
    const boundIds = new Set<string>();
    for (const control of controls) {
      if (control.type === 'speaker' && control.controlBindings) {
        for (const id of Object.keys(control.controlBindings)) boundIds.add(id);
      }
    }
    for (const control of controls) {
      const isSpeakerIdentity =
        (!!control.id && boundIds.has(control.id)) ||
        control.default?.quote === QuoteControlType.NAME ||
        control.default?.quote === QuoteControlType.ORG ||
        control.default?.quote === QuoteControlType.HEADSHOT;
      // Clear only the identity text + headshot — leave styling (color/font) alone.
      const isClearable =
        control.type === 'text' || control.type === 'headshot';
      if (isSpeakerIdentity && isClearable) {
        suggestions.set(control, { value: '', reason });
      }
    }
  }

  return suggestions;
}

/**
 * Suggest a cleaned-up field label by stripping an auto-generated suffix — a
 * trailing "-<token>" where the token looks like a random id (mixed case + at
 * least one digit, length >= 5), e.g. "Name-BXGEnb3" -> "Name". Real hyphenated
 * labels like "Company-Title" or "Lower-Third" are preserved. Returns null when
 * no fix applies.
 */
export function suggestLabelFix(label: string): string | null {
  const match = /^(.*[^-])-([A-Za-z0-9]{5,})$/.exec(label);
  if (!match) return null;
  const [, base, token] = match;
  const looksLikeRandomId =
    /\d/.test(token) && /[A-Z]/.test(token) && /[a-z]/.test(token);
  return looksLikeRandomId ? base : null;
}

/**
 * A change to apply to a control when updating standards: a new `value`
 * (customValue) and/or a corrected `label`. Matched to a control by `property` +
 * `elementPaths`.
 */
export interface ControlOverride {
  elementPaths: number[][];
  property: string;
  /** The new standard; `null` removes it so the default applies again. */
  value?: unknown;
  /** Renames the ELEMENT (only for its own `text` / `source` row). */
  label?: string;
  /**
   * The control's own label (PLAT-11993): a non-blank string sets
   * `customLabel`, `''` clears it so the label is derived again.
   */
  customLabel?: string;
  /** Shows (true) or hides (false) the control in the editor (PLAT-11993). */
  enabled?: boolean;
  /** A speaker control's bindings (PLAT-11993): which fields it fills. */
  controlBindings?: Record<string, SpeakerRole>;
}

export type SpeakerRole = SpeakerBindingKey;

// What a field is for, by its quote default — how a binding is rebuilt after
// it was switched off (the speaker control no longer names it).
const QUOTE_SPEAKER_ROLE: Partial<Record<QuoteControlType, SpeakerRole>> = {
  [QuoteControlType.NAME]: 'name',
  [QuoteControlType.ORG]: 'title',
  [QuoteControlType.HEADSHOT]: 'headshot',
};
const SPEAKER_ROLE_ORDER: SpeakerRole[] = [
  'name',
  'firstName',
  'lastName',
  'title',
  'role',
  'organization',
  'roleLine',
  'headshot',
];

export interface SpeakerBindingOption {
  controlId: string;
  role: SpeakerRole;
  /** The bound field's label, e.g. "Name-Tag-Name". */
  label: string;
  /** True when the speaker control fills this field today. */
  bound: boolean;
}

/**
 * Every field a speaker control fills, or could fill (PLAT-11993): the ones
 * it is bound to, plus name / title / headshot fields it was unbound from
 * (found by their quote default), so a switched-off binding can be switched
 * back on. Only controls with an `id` can be bound.
 */
export function speakerBindingOptions(
  controls: ControlConfiguration[],
): SpeakerBindingOption[] {
  const speaker = controls.find(
    (c): c is SpeakerControlConfiguration => c.type === 'speaker',
  );
  if (!speaker) return [];
  const bindings = speaker.controlBindings ?? {};
  const options: SpeakerBindingOption[] = [];
  controls.forEach(control => {
    if (!control.id || control.type === 'speaker') return;
    const role =
      bindings[control.id] ??
      (control.default?.quote
        ? QUOTE_SPEAKER_ROLE[control.default.quote]
        : undefined);
    if (!role) return;
    if (options.some(o => o.controlId === control.id)) return;
    options.push({
      controlId: control.id,
      role,
      label: control.label,
      bound: control.id in bindings,
    });
  });
  return options.sort(
    (a, b) =>
      SPEAKER_ROLE_ORDER.indexOf(a.role) - SPEAKER_ROLE_ORDER.indexOf(b.role),
  );
}

/**
 * The bindings a speaker control applies: none while it is switched off
 * (PLAT-11993), so neither picking nor auto-resolving a speaker fills fields.
 */
export function activeSpeakerBindings(
  speaker:
    | Pick<SpeakerControlConfiguration, 'controlBindings' | 'enabled'>
    | undefined,
): Record<string, SpeakerRole> {
  if (!speaker || speaker.enabled === false) return {};
  return speaker.controlBindings ?? {};
}

/**
 * Apply label overrides by renaming the SOURCE element at each override's path
 * (a primary control's label IS its element's name). `saveCompositionTemplate`
 * then re-derives every control label from these names via
 * `normalizeTemplateControls`, so the edit persists instead of being overwritten.
 * Returns the source unchanged when there are no label overrides; otherwise a
 * deep clone with the renamed elements (the input is never mutated).
 */
export function applyLabelOverridesToSource<T>(
  source: T,
  overrides: ControlOverride[],
): T {
  // Only an element's own row (its text / image) renames the element. A
  // setting's label is its control's `customLabel`; letting it through here
  // would rename the element to "Name Color" (PLAT-11993).
  const labelOverrides = overrides.filter(
    o =>
      o.label !== undefined &&
      o.elementPaths.length > 0 &&
      (o.property === 'text' || o.property === 'source'),
  );
  if (labelOverrides.length === 0) return source;
  const clone = JSON.parse(JSON.stringify(source)) as T;
  for (const o of labelOverrides) {
    const el = resolveElementFromPath(clone as ElementLike, o.elementPaths[0]);
    if (el) (el as { name?: string }).name = o.label;
  }
  return clone;
}

export type StandardsValueEditor = 'text' | 'color' | 'font' | 'fontSize';

/**
 * The editor a control's value gets in the Set as Graphic Standard modal, or
 * undefined when it stays read-only. Text is editable except the quote body
 * (each card's own words); images and other numbers stay read-only.
 */
export function standardsValueEditor(
  control: Pick<ControlConfiguration, 'type' | 'property' | 'default'>,
): StandardsValueEditor | undefined {
  if (control.type === 'text') {
    return control.default?.quote === QuoteControlType.TEXT
      ? undefined
      : 'text';
  }
  if (control.type === 'color') return 'color';
  if (control.type === 'font') return 'font';
  if (control.type === 'number' && control.property === 'font_size') {
    return 'fontSize';
  }
  return undefined;
}

export interface ControlDiffRow {
  control: ControlConfiguration;
  label: string;
  /** The actual current value (resolved from the live element), not the stored template default. */
  before: unknown;
  /** The value about to be applied: a rule suggestion, else a pending manual edit, else unchanged. */
  after: unknown;
  /** Rationale, present only when `after` comes from a recommendation rule. */
  reason?: string;
  /** True when `after` was set by a recommendation rule. */
  recommended: boolean;
  /** True when `after` differs from `before`. */
  changed: boolean;
  /** True when the After value can be edited in the modal. */
  editable: boolean;
  /**
   * Which editor the After value uses (PLAT-11993): free text, the colour or
   * font picker, or the font-size stepper. Absent for read-only rows.
   */
  editor?: StandardsValueEditor;
  /**
   * True when the template ships this control switched off (`enabled:
   * false`): it is listed so it can be switched on, but nothing about it is
   * recommended or edited while it is off.
   */
  disabled?: boolean;
  /** True when the row's label can be edited. Every row's can (PLAT-11993);
   *  what an edit means depends on `labelKind`. */
  labelEditable: boolean;
  /**
   * `element`: the row IS the element (its text / image) and its label is the
   * element's name — editing it renames the element, and settings without a
   * custom label follow. `setting`: the label is the control's own, saved as
   * its `customLabel`.
   */
  labelKind: 'element' | 'setting';
  /** A cleaner field label suggested by the label-normalization rule, if any
   *  (element rows only). */
  suggestedLabel?: string;
}

// Compare two control values for the diff. Color values are compared
// case-insensitively (#FFFFFF === #ffffff): the renderer and the stored standard
// can drift only in hex casing, which is not a real change — otherwise it would
// read as `changed`, enabling Apply for a no-op and showing a spurious row.
/**
 * Normalise a control value for comparison. Colours compare case-insensitively.
 * A number with an optional unit ("22.87 vmin", "2.5925925925925926 vh", 20,
 * "20") compares on four decimals and the unit, so a value that only drifted in
 * floating-point noise between the renderer and the stored standard is not a
 * change (PLAT-11977). Everything else compares as its trimmed string.
 */
const normalizeControlValue = (
  control: Pick<ControlConfiguration, 'type'>,
  v: unknown,
): string => {
  if (v === undefined || v === null) return '';
  const s = String(v).trim();
  if (control.type === 'color') return s.toLowerCase();
  const m = /^(-?\d+(?:\.\d+)?)\s*([a-z%]*)$/i.exec(s);
  if (m) return `${Number(Number(m[1]).toFixed(4))}${m[2].toLowerCase()}`;
  return s;
};

/** True when two values of a control read as the same standard. */
export const controlValuesEqual = (
  control: Pick<ControlConfiguration, 'type'>,
  a: unknown,
  b: unknown,
): boolean =>
  normalizeControlValue(control, a) === normalizeControlValue(control, b);

const valuesDiffer = (
  control: ControlConfiguration,
  a: unknown,
  b: unknown,
): boolean => !controlValuesEqual(control, a, b);

/**
 * Build the Update Standards before/after rows. `before` is the control's
 * current saved template standard (`resolveStandardValue`); `after` is a rule
 * recommendation when one applies, otherwise the user's pending manual edit
 * (from `updatedValues`), otherwise unchanged. This is what makes the modal
 * reflect changes the user made in the panel before opening it. Speaker controls
 * are skipped — their name/title/headshot surface through bound text/headshot
 * rows. Only label/name text is editable; the quote body (`default.quote ===
 * TEXT`) and non-text controls are read-only.
 */
export function buildStandardsDiffRows(params: {
  composition: Pick<CompositionTemplate, 'compositionType'>;
  controls: ControlConfiguration[];
  updatedValues: {
    elementPaths: number[][];
    value: unknown;
    property: string;
  }[];
  organization?: OrganizationContext;
  story?: Story;
  /** Pull quotes keep their speaker — suppresses the "hide speaker" rule. */
  isPullQuote?: boolean;
}): ControlDiffRow[] {
  const {
    composition,
    controls,
    updatedValues,
    organization,
    story,
    isPullQuote,
  } = params;
  const suggestions = computeStandardsSuggestions(
    composition,
    controls,
    isPullQuote,
  );

  // Switched-off controls are listed too (PLAT-11993), so they can be
  // switched on; they get no recommendation while off.
  return controls
    .filter(control => control.type !== 'speaker')
    .map(control => {
      const disabled = control.enabled === false;
      const before = resolveStandardValue(control, organization, story);
      const suggestion = disabled ? undefined : suggestions.get(control);
      const manual = updatedValues.find(entry =>
        matchesControl(entry, control),
      );
      const after = suggestion
        ? suggestion.value
        : manual
          ? manual.value
          : before;
      return {
        control,
        label: control.label,
        before,
        after,
        reason: suggestion?.reason,
        recommended: !!suggestion,
        changed: valuesDiffer(control, before, after),
        editable: !!standardsValueEditor(control),
        editor: standardsValueEditor(control),
        disabled,
        labelEditable: true,
        labelKind: isElementRow(control) ? 'element' : 'setting',
        suggestedLabel: isElementRow(control)
          ? (suggestLabelFix(control.label) ?? undefined)
          : undefined,
      };
    });
}

/** A control on the element's own content (its text or image): its label is
 *  the element's name. */
const isElementRow = (control: Pick<ControlConfiguration, 'property'>) =>
  control.property === 'text' || control.property === 'source';

/**
 * Resolve a control's effective "standard" value — the value saved on the
 * template (`customValue`), falling back to its default with brand-kit / `{value}`
 * resolution. Mirrors the comparison baseline in `calculateUpdatedControlValues`.
 */
export function resolveStandardValue(
  control: ControlConfiguration,
  organization?: OrganizationContext,
  story?: Story,
): unknown {
  let value: unknown = control.customValue ?? control.default;
  if (value && typeof value === 'object' && 'brandKit' in value) {
    value = getDefaultControlValue(control, organization, story);
  } else if (value && typeof value === 'object' && 'value' in value) {
    value = (value as { value: unknown }).value;
  }
  // A standard value should resolve to a primitive (or undefined). Some controls
  // carry a non-value config object as their default — e.g. the quote body's
  // `{ quote: QuoteControlType.TEXT }`. Returning that object would render as
  // "[object Object]" in the Before/After columns (displayValue/asText String()
  // it), so fall back to undefined and let those columns show "—".
  if (value && typeof value === 'object') return undefined;
  return value;
}

/**
 * Build before/after rows for the "Apply Standards" preview (template →
 * composition): `before` is the control's current value, `after` is its resolved
 * template standard. The current value prefers a pending manual edit
 * (`updatedValues`, updated synchronously) over the live element value
 * (`getUnifiedValue`, which lags behind the debounced renderer sync) so a
 * just-made change shows immediately. Read-only — applying enforces the saved
 * standard, it doesn't redefine it. Speaker controls are skipped (their bound
 * text/headshot controls carry the values).
 */
export function buildApplyStandardsRows(params: {
  controls: ControlConfiguration[];
  activeElement: ElementState;
  extraElementData?: Record<string, unknown>;
  updatedValues?: {
    elementPaths: number[][];
    value: unknown;
    property: string;
  }[];
  organization?: OrganizationContext;
  story?: Story;
}): ControlDiffRow[] {
  const {
    controls,
    activeElement,
    extraElementData,
    updatedValues,
    organization,
    story,
  } = params;
  return controls
    .filter(control => control.enabled !== false && control.type !== 'speaker')
    .map(control => {
      const manual = updatedValues?.find(entry =>
        matchesControl(entry, control),
      );
      const before = manual
        ? manual.value
        : getUnifiedValue(control, activeElement, extraElementData)
            .unifiedValue;
      const after = resolveStandardValue(control, organization, story);
      return {
        control,
        label: control.label,
        before,
        after,
        reason: undefined,
        recommended: false,
        changed: valuesDiffer(control, before, after),
        editable: false,
        // Apply Standards is read-only (enforce, not redefine) — never editable.
        labelEditable: false,
        labelKind: isElementRow(control) ? 'element' : 'setting',
      };
    });
}

/**
 * Apply a template's saved standard control values onto a live element (template
 * → composition). Resolves each control's standard value and pushes it via
 * `modifyCompositionProperty`. Speaker controls and element placement are left
 * untouched.
 */
export function applyTemplateStandardsToElement(params: {
  elementId: string;
  editorStore: EditorStoreLike;
  controls: ControlConfiguration[] | undefined;
  organization?: OrganizationContext;
  story?: Story;
}): void {
  const { elementId, editorStore, controls, organization, story } = params;
  if (!elementId || !controls) return;
  // Snapshot-based undo (VideoCreatorStore) captures ONE full-state snapshot
  // *before* the first command that requests an undo entry. So let only the
  // first applied control create that entry and the rest skip it — a single Undo
  // then reverts the whole Apply at once, instead of one step per control.
  // (Normalized templates have one element path per control, so this is exactly
  // one snapshot.)
  let createUndoRedoEntry = true;
  controls
    .filter(control => control.enabled !== false && control.type !== 'speaker')
    .forEach(control => {
      const value = resolveStandardValue(control, organization, story);
      if (value === undefined || value === null) return;
      modifyCompositionProperty(
        elementId,
        editorStore,
        control.elementPaths,
        control.property,
        value,
        createUndoRedoEntry,
      );
      createUndoRedoEntry = false;
    });
}

/**
 * Apply default control values into an in-memory composition source object before element creation.
 * Traverses elementPaths similarly to modifyCompositionProperty, but mutates the provided source tree object.
 * Only applies when control.default.value is defined (not null/undefined) and elementPaths resolve to valid child ids.
 *
 * @param originalValueOverrides - Optional function to compute use-case-specific value overrides.
 *   Called for each control to provide custom values that take precedence over default values.
 *   For example, quote cards use this to inject quote text, name, org, and background.
 * @param sourceModifications - Optional function to apply additional modifications to the source
 *   after control values are applied (e.g., dimension overrides for quote cards).
 */
export function applyDefaultControlValuesToSource({
  source,
  controls,
  organization,
  story,
  convertToVh,
  originalValueOverrides,
  sourceModifications,
}: {
  source: Record<string, unknown>;
  controls: ControlConfiguration[] | undefined | null;
  organization?: OrganizationContext;
  story?: Story;
  convertToVh?: ConvertToVhFn;
  originalValueOverrides?: (control: ControlConfiguration) => unknown;
  sourceModifications?: (source: Record<string, unknown>) => void;
}): Record<string, unknown> {
  if (!source || typeof source !== 'object') return source;
  if (!Array.isArray(controls) || controls.length === 0) return source;

  const rootElement: ElementLike = isElementLike(source)
    ? source
    : { id: 'root' };

  for (const control of controls) {
    if (!control || typeof control !== 'object') continue;
    if (typeof control.property !== 'string') continue;
    if (control.enabled === false) continue;

    const originalValue = getDefaultControlValue(control, organization, story);

    // override value takes precedence over custom value and original value
    const overrideValue = originalValueOverrides
      ? originalValueOverrides(control)
      : undefined;

    const customValue = control?.customValue;
    const value = overrideValue ?? customValue ?? originalValue;

    if (value === null || typeof value === 'undefined' || value === 'undefined')
      continue;
    if (
      !Array.isArray(control.elementPaths) ||
      control.elementPaths.length === 0
    )
      continue;
    if (!rootElement) continue;
    const isImageOrLogo = control.type === 'image' || control.type === 'logo';
    const hasExplicitDefault =
      !!control.default &&
      typeof control.default === 'object' &&
      ('value' in control.default || 'brandKit' in control.default);
    const hasUserOverride =
      control.customValue !== undefined && control.customValue !== null;
    // Image/logo controls without an explicit default should preserve the
    // source element's existing asset rather than be overwritten by the
    // brand-kit logo fallback.
    const preserveImageSourceValue =
      isImageOrLogo && !hasExplicitDefault && !hasUserOverride;
    // Font controls: when the value being applied is only a brand *default*
    // (no explicit user pick and no use-case override), don't clobber a font
    // the template element already declares. The brand default font (e.g. the
    // brand-kit `defaultFont`) is a bare family name that the postScript-keyed
    // renderer often can't match — and it isn't always registered with the
    // renderer at all — so overwriting an intentional, registered template
    // font with it makes the element silently fall back. A real user pick
    // (customValue) or use-case override still applies as before.
    const isFontBrandDefaultOnly =
      control.type === 'font' &&
      control.default?.brandKit === DefaultBrandKitValue.DEFAULT_FONT &&
      overrideValue === undefined &&
      !hasUserOverride;
    const preserveSourceValue =
      preserveImageSourceValue || isFontBrandDefaultOnly;

    for (const path of control.elementPaths) {
      if (!Array.isArray(path) || path.length === 0) continue;
      const targetEl = resolveElementFromPath(rootElement, path);
      if (!targetEl) continue;

      if (preserveSourceValue) {
        const existing = (targetEl as Record<string, unknown>)[
          control.property
        ];
        if (existing !== undefined && existing !== null && existing !== '') {
          continue;
        }
      }

      const payload = maybeConvertFontSize(
        control.property,
        value,
        convertToVh,
      );
      for (const [k, v] of Object.entries(payload)) {
        Reflect.set(targetEl, k, v);
      }

      // Keep font_weight consistent with the chosen brand variant. A font
      // control writes only font_family (the variant's postScriptName); the
      // matching brand font's weight is written too, so a single Font control
      // fully specifies the face on re-apply with no separate Font Weight
      // control. No brand match (popular font / bare family name) → leave the
      // element's existing weight untouched.
      if (control.type === 'font' && typeof value === 'string') {
        const brandFont = getCustomFontFromPostScriptName(
          value,
          organization?.branding?.customFonts ?? undefined,
        );
        if (brandFont) {
          // Match the renderer's face registration, keyed by
          // (postScriptName, weight, style): write weight AND style so an
          // italic / non-default-weight variant renders correctly on re-apply
          // (otherwise an italic face falls back to upright).
          Reflect.set(targetEl, 'font_weight', String(brandFont.usWeightClass));
          Reflect.set(targetEl, 'font_style', brandFont.style);
        }
      }
    }
  }

  // Apply any additional source modifications (e.g., dimensions)
  if (sourceModifications) {
    sourceModifications(source);
  }

  return source;
}

/**
 * Normalizes a plain speaker object so that `headshot` is always present when
 * `headshots[]` contains at least one entry.  Control bindings reference the
 * singular `headshot` key, but the DatoCMS model stores an array under
 * `headshots`.  This bridges the two shapes.
 *
 * Mutates and returns the same object for convenience.
 */
export function normalizeSpeakerForControlBindings(
  speaker: Record<string, unknown>,
): Record<string, unknown> {
  if (
    !('headshot' in speaker) &&
    Array.isArray(speaker.headshots) &&
    speaker.headshots.length > 0
  ) {
    speaker.headshot = speaker.headshots[0];
  }
  return speaker;
}

/**
 * Applies a mapping of meta control identifiers to input value keys.
 * Example:
 *  controlBindings = { "headlineText": "title", "accentColor": "brandColor" }
 *  inputValuesObject = { title: "Breaking News", brandColor: "#FF0000" }
 * Will find controls whose control.id matches 'headlineText' & 'accentColor'
 * and call modifyCompositionProperty for each with the mapped value.
 */
export function applySpeakerControlValueMappingToControls(
  elementId: string,
  editorStore: EditorStoreLike,
  controls: ControlConfiguration[],
  controlBindings: Record<string, string>,
  inputValuesObject: { [key: string]: unknown | null }, // Accept any keyed object of values (may include nulls)
  createUndoRedoEntry: boolean = true,
  // The workspace's Speaker Format (`branding.speakerFormat`, PLAT-12176).
  // Absent = the default: "First Last", "Company | Position".
  speakerFormat?: unknown,
): void {
  if (!elementId) return;
  if (!controls || controls.length === 0) return;
  const pairs = Object.entries(controlBindings);
  if (pairs.length === 0) return;

  const controlIndex: Record<string, ControlConfiguration> = {};
  for (const c of controls) {
    if (c.id) controlIndex[c.id] = c;
  }

  // The speaker's two lines (PLAT-12176): Name and Title as typed, else
  // derived from the facts in the workspace's Speaker Format. `roleLine`
  // (phase 2) is the Title line by its old name.
  //
  // A three-line template saved before Position existed binds Title and
  // Company but no Position: its Title element is the Position line, so it
  // shows the Position, or the stored Title when a speaker has none — exactly
  // what it showed before.
  const values = Object.values(controlBindings);
  const titleIsPosition =
    values.includes('organization') && !values.includes('role');
  const asText = (v: unknown) => (typeof v === 'string' ? v : null);
  const speaker = {
    name: asText(inputValuesObject.name),
    firstName: asText(inputValuesObject.firstName),
    lastName: asText(inputValuesObject.lastName),
    title: asText(inputValuesObject.title),
    role: asText(inputValuesObject.role),
    organization: asText(inputValuesObject.organization),
  };
  const has = (...keys: string[]) => keys.some(k => k in inputValuesObject);

  for (const [controlId, valueKey] of pairs) {
    const control = controlIndex[controlId];
    if (!control) continue; // unmapped control id
    let value: unknown;
    if (valueKey === 'name') {
      if (!has('name', 'firstName', 'lastName')) continue;
      value = speakerNameLine(speaker, speakerFormat);
    } else if (valueKey === 'title' && titleIsPosition) {
      if (!has('title', 'role')) continue;
      value = speaker.role?.trim() || speaker.title || '';
    } else if (valueKey === 'title' || valueKey === 'roleLine') {
      if (!has('title', 'role', 'organization')) continue;
      value = speakerTitleLine(speaker, speakerFormat);
    } else {
      if (!(valueKey in inputValuesObject)) continue; // value key absent
      value = inputValuesObject[valueKey];
    }
    // For headshot controls, use a default placeholder when the speaker has no headshot
    if (
      (value === null || typeof value === 'undefined') &&
      (control.type === 'headshot' || /headshot/i.test(control.label))
    ) {
      value = DEFAULT_HEADSHOT_PLACEHOLDER;
    }
    // A bound text line the speaker lacks renders empty (DE5); skipping it
    // would leave the previous speaker's value on the line.
    if (
      (value === null || typeof value === 'undefined') &&
      control.type === 'text'
    ) {
      value = '';
    }
    if (value === null || typeof value === 'undefined') continue; // skip null/undefined values
    modifyCompositionProperty(
      elementId,
      editorStore,
      control.elementPaths,
      control.property,
      value,
      createUndoRedoEntry,
    );
  }
}

/**
 * Creates a composition element in the editor, applying default control values and speaker resolution.
 * Returns the created element (if any) and its id.
 *
 * UI-specific selection and setActiveElements should be handled by the caller.
 *
 * @deprecated Use `createCompositionElement` in `@src/services/composition/CompositionElementService`.
 *             This legacy utility will be removed after migration (#phase:P3).
 */
export async function createCompositionElementInEditor({
  template,
  videoCreator,
  editorStore,
  datoClientStore,
}: {
  template: CompositionTemplate;
  videoCreator: IVideoCreatorStore;
  editorStore: EditorStoreLike;
  datoClientStore: DatoClientStore;
}): Promise<{ element: any; elementId: string | undefined } | undefined> {
  // Clone source to avoid mutating original composition definition
  const adjustedComposition =
    CompositionNormalizer.adjustCompositionSourceByAspectConfiguration(
      template,
      videoCreator.getCurrentAspectRatio(),
    );
  // Apply control-level default values directly into the cloned source tree
  applyDefaultControlValuesToSource({
    source: adjustedComposition.source,
    controls: template.controls,
    organization: videoCreator.organization,
    story: videoCreator.story,
    convertToVh: (v: string) => videoCreator.textProducer.convertToVh(v),
  });

  const props = {
    time: videoCreator.time,
    templateId: template.id,
    duration: 4,
    ...adjustedComposition.source,
  };

  let element: Element | null = null;

  if (template.placement === CompositionPlacement.END) {
    const videoDuration = videoCreator.duration;
    if (typeof videoDuration === 'number' && !Number.isNaN(videoDuration)) {
      props.time = videoDuration;
    }
    element = editorStore.editor.commandCreateCompositionElement(
      props,
      undefined,
      TimelineMode.DEFAULT,
    );
  } else if (template.placement === CompositionPlacement.START) {
    props.time = 0;
    element = editorStore.editor.commandCreateCompositionElement(
      props,
      undefined,
      TimelineMode.RIPPLE_ALL_TRACKS,
    );
  } else if (template.placement === CompositionPlacement.SECTION) {
    // SECTION placement: insert at end of current clip with ripple
    const sectionResult = calculateSectionInsertionTime(
      editorStore,
      videoCreator.time,
      videoCreator.duration,
      template.overlapBefore ?? 0,
    );

    if (!sectionResult) {
      // No clip at current time, fall back to CURRENT_TIME behavior
      element = editorStore.editor.commandCreateCompositionElement(
        props,
        undefined,
        TimelineMode.DEFAULT,
      );
    } else if (sectionResult.isAtEnd) {
      // Clip at end (and no previous clip), behave like END placement
      props.time = sectionResult.insertionTime;
      element = editorStore.editor.commandCreateCompositionElement(
        props,
        undefined,
        TimelineMode.DEFAULT,
      );
    } else {
      // Insert at calculated section point with ripple
      props.time = sectionResult.insertionTime;
      const rippleConfig: RippleConfig = {
        overlapBefore: template.overlapBefore ?? 0,
        overlapAfter: template.overlapAfter ?? 0,
      };
      element = editorStore.editor.commandCreateCompositionElement(
        props,
        undefined,
        TimelineMode.RIPPLE_ALL_TRACKS,
        true,
        rippleConfig,
      );
    }
  } else {
    element = editorStore.editor.commandCreateCompositionElement(
      props,
      undefined,
      TimelineMode.DEFAULT,
    );
  }

  if (element && typeof element.id === 'string' && videoCreator.currentVideo) {
    // Associate composition template id with this element via utility (preserves other buckets)
    storeCompositionTemplateMetadata(
      videoCreator.currentVideo,
      element.id,
      template,
    );

    // UI selection and activation (was previously in component)
    setTimeout(async () => {
      if (element && typeof element.id === 'string') {
        editorStore.selectElement(element.id);
        await videoCreator.setActiveElements(element.id);
      }
    }, 300);

    // Resolve speaker at current timeline time (best-effort) via service orchestrator
    await resolveAndApplySpeakerForCompositionElement({
      elementId: element.id,
      editorStore,
      datoClientStore,
      videoCreator,
    });
    return { element, elementId: element.id };
  }
  return undefined;
}

/**
 * Resolve speaker at a given timeline time and apply its mapped values to a composition's speaker control.
 * Shared between initial composition creation and later element movements (speaker should reflect new position).
 */
export function getCompositionControlsForElement(opts: {
  videoCreator: { currentVideo?: { extraElementData?: Record<string, any> } };
  elementId: string;
}): ControlConfiguration[] {
  const { videoCreator, elementId } = opts;
  const meta = getElementMetaObject(videoCreator.currentVideo, elementId);
  if (meta && typeof meta === 'object') {
    const raw = (meta as Record<string, unknown>).compositionControls;
    if (Array.isArray(raw)) {
      // Best-effort structural refinement: ensure objects with at least a property string and elementPaths array
      return raw.filter(
        r => r && typeof r === 'object',
      ) as ControlConfiguration[];
    }
  }
  return [];
}

/**
 * Refills a nametag already on the canvas from controls just saved
 * (PLAT-12126): Set as Graphic Standard writes the template's bindings and
 * role-line settings, but the element keeps the text it was filled with
 * until it is moved, trimmed or given a speaker. This runs the bindings once
 * more, with the given controls, for the speaker the element already shows
 * (its `speakerLabel` meta, which a manual pick sets too), falling back to
 * whoever speaks at the element's own time. A switched-off speaker control
 * fills nothing, as everywhere else.
 */
export async function refillSpeakerForCompositionElement(params: {
  elementId: string;
  editorStore: EditorStoreLike;
  datoClientStore: any; // minimal duck type for service
  videoCreator: {
    storyId?: string;
    time: number;
    rootStore: { transcriptionStore?: any };
    currentVideo?: { extraElementData?: Record<string, any> };
    // The showcase, for its Speaker Format (PLAT-12176).
    organization?: { branding?: { speakerFormat?: unknown } | null } | null;
  };
  controls: ControlConfiguration[];
  timelineTime?: number; // the element's start; the playhead when absent
}): Promise<boolean> {
  const {
    elementId,
    editorStore,
    datoClientStore,
    videoCreator,
    controls,
    timelineTime,
  } = params;
  if (!elementId) return false;
  const speakerControl = controls.find(
    (c): c is SpeakerControlConfiguration => c.type === 'speaker',
  );
  if (!speakerControl) return false;
  const meta = getElementMetaObject(videoCreator.currentVideo, elementId);
  const label =
    typeof meta?.speakerLabel === 'string' ? meta.speakerLabel : undefined;
  try {
    const result = await transcriptSpeakerService.resolveSpeakerAtTime({
      storyId: videoCreator.storyId,
      timeSeconds:
        typeof timelineTime === 'number' && Number.isFinite(timelineTime)
          ? timelineTime
          : videoCreator.time,
      getTranscriptElements: () =>
        videoCreator.rootStore.transcriptionStore?.getFinalTranscriptionElements?.(),
      datoClientStore,
    });
    const speaker =
      (label ? result?.speakersIndex?.[label] : undefined) ?? result?.speaker;
    if (!speaker) return false;
    const speakerPlain: Record<string, unknown> = { ...speaker };
    normalizeSpeakerForControlBindings(speakerPlain);
    applySpeakerControlValueMappingToControls(
      elementId,
      editorStore,
      controls,
      activeSpeakerBindings(speakerControl),
      speakerPlain,
      false,
      videoCreator.organization?.branding?.speakerFormat,
    );
    return true;
  } catch (error) {
    logger.warn('Failed to refill speaker for composition element', {
      elementId,
      error,
    });
    return false;
  }
}

export async function resolveAndApplySpeakerForCompositionElement(params: {
  elementId: string;
  editorStore: EditorStoreLike;
  datoClientStore: any; // minimal duck type for service
  videoCreator: {
    storyId?: string;
    time: number;
    rootStore: { transcriptionStore?: any };
    currentVideo?: { extraElementData?: Record<string, any> };
    // The showcase, for its Speaker Format (PLAT-12176).
    organization?: { branding?: { speakerFormat?: unknown } | null } | null;
  };
  timelineTime?: number; // optional override for timeSeconds
  transcriptElements?: TranscriptElementWithClipSegmentAttributes[] | null;
}): Promise<boolean> {
  const {
    elementId,
    editorStore,
    datoClientStore,
    videoCreator,
    timelineTime,
    transcriptElements,
  } = params;
  if (!elementId) return false;

  const compositionControls = getCompositionControlsForElement({
    videoCreator,
    elementId,
  });
  if (!Array.isArray(compositionControls)) return false;

  const speakerControl = compositionControls.find(
    (c): c is SpeakerControlConfiguration => c.type === 'speaker',
  );

  try {
    const result = await transcriptSpeakerService.resolveSpeakerAtTime({
      storyId: videoCreator.storyId,
      timeSeconds:
        typeof timelineTime === 'number' ? timelineTime : videoCreator.time,
      getTranscriptElements: () =>
        transcriptElements ||
        videoCreator.rootStore.transcriptionStore?.getFinalTranscriptionElements?.(),
      datoClientStore,
    });

    if (result?.speaker) {
      const speakerPlain: Record<string, unknown> = {};
      if (result.speaker && typeof result.speaker === 'object') {
        for (const [k, v] of Object.entries(result.speaker)) {
          speakerPlain[k] = v;
        }
      }
      normalizeSpeakerForControlBindings(speakerPlain);
      applySpeakerControlValueMappingToControls(
        elementId,
        editorStore,
        compositionControls,
        activeSpeakerBindings(speakerControl),
        speakerPlain,
        false,
        videoCreator.organization?.branding?.speakerFormat,
      );
      // Persist the resolved speaker label (id) onto extraElementData meta for later retrieval
      if (videoCreator.currentVideo) {
        const speakerId = speakerPlain.id;
        if (typeof speakerId === 'string' && speakerId.trim()) {
          upsertElementMetaObject(videoCreator.currentVideo, elementId, {
            speakerLabel: speakerId,
          });
        }
      }
      return true;
    }
  } catch (error) {
    logger.warn('Failed to resolve/apply speaker for composition element', {
      elementId,
      error,
    });
  }
  return false;
}

export async function saveCompositionTemplate(
  composition: CompositionTemplate,
  newCompositionName: string,
  updatedControls: ControlConfiguration[],
  showcase: Showcase | undefined,
  compositionRepository: CompositionRepository,
  subcategoryId: string,
  personId: string,
  action: CompositionActions,
): Promise<CompositionTemplate | undefined> {
  if (!showcase || !personId || !newCompositionName || !updatedControls)
    return undefined;

  const updatedComposition = {
    ...composition,
    label: newCompositionName,
    // Persist a clean source: drop any injected font face the composition no
    // longer uses (getCustomFontsSource injects every brand variant at render
    // time). Uses element font_family + Font control picks; never drops a
    // referenced face, and rendering re-injects what it needs.
    source: pruneUnusedSourceFonts(composition.source, updatedControls),
    // Enforce the template control standard on save: per-element font controls
    // (split shared) ordered Text → Text Color → Font.
    controls: normalizeTemplateControls(composition.source, updatedControls),
    // Save Changes (update) must NOT rename an existing template — preserve its
    // internal name. Only new templates (create / save_as) get a computed name.
    internalName:
      action === 'update'
        ? (composition.internalName ??
          `Customer | ${showcase?.title || ''} | ${newCompositionName}`)
        : `Customer | ${showcase?.title || ''} | ${newCompositionName}`,
  };

  let newCompositionResponse: CompositionTemplate | undefined = undefined;
  if (action === 'create' || action === 'save_as') {
    let compositionId =
      action === 'create'
        ? composition.id
        : composition.parentComposition?.id || composition.id;

    newCompositionResponse =
      await compositionRepository.createCustomCompositionTemplate(
        updatedComposition,
        subcategoryId,
        personId,
        showcase?.id,
        compositionId,
      );
  } else if (action === 'update') {
    newCompositionResponse =
      await compositionRepository.updateCustomCompositionTemplate(
        updatedComposition,
        personId,
      );
  }

  return newCompositionResponse;
}
